import React, { useState, useEffect, useContext } from 'react';
import { Search, Plus, Eye, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getData, postData, serverURL } from '../../../../services/Fetchnodeservices';
// ⚠️ IMPORTANT: Adjust this path to exactly match where your ThemeContext is located!
import { ThemeContext } from '../../../../context/ThemeContext';

export default function RestaurantDashboard() {
  const navigate = useNavigate();
  
  // 🛡️ SAFE CONTEXT CONSUMPTION WITH FALLBACK
  const themeContext = useContext(ThemeContext);
  const isDark = themeContext?.currentTheme === 'dark';
  
  const fallbackTheme = {
    bg: isDark ? "#0f172a" : "#f8fafc",
    surface: isDark ? "#1e293b" : "#ffffff",
    surfaceAlt: isDark ? "#334155" : "#f1f5f9",
    border: isDark ? "#475569" : "#e2e8f0",
    text: isDark ? "#f8fafc" : "#0f172a",
    textSub: isDark ? "#94a3b8" : "#64748b",
    textMuted: isDark ? "#64748b" : "#94a3b8",
    primary: isDark ? "#818cf8" : "#6366f1",
    primaryLight: isDark ? "rgba(129, 140, 248, 0.2)" : "rgba(99, 102, 241, 0.1)",
    success: "#22c55e",
    successLight: isDark ? "rgba(34, 197, 94, 0.2)" : "rgba(34, 197, 94, 0.1)",
    successText: isDark ? "#86efac" : "#166534",
    danger: "#ef4444",
    dangerLight: isDark ? "rgba(239, 68, 68, 0.2)" : "rgba(239, 68, 68, 0.1)",
    dangerText: isDark ? "#fca5a5" : "#991b1b",
  };

  const theme = themeContext?.theme || fallbackTheme;

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [menuFilter, setMenuFilter] = useState('All');
  const [visibleRows, setVisibleRows] = useState({});
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuLoading, setMenuLoading] = useState(null);
  const [stats, setStats] = useState({
    totalMenus: 0,
    totalItems: 0,
    activeMenus: 0,
    pending: 0
  });

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getData("Menu/get_restaurant_settings");
      
      if (response && response.status) {
        if (response.data && response.data.length > 0) {
          const transformedData = response.data.map(item => ({
            id: item.id,
            name: item.organisation_name || 'N/A',
            contact: item.contact || 'N/A',
            items: item.item_count || 0,
            direction: getDirectionText(item),
            commission: getCommissionText(item),
            status: Number(item.status) === 1 ? "Active" : Number(item.status) === 0 ? "Inactive" : "",
            is_menu: item.is_menu || 0,
            updated: item.updated_at ? new Date(item.updated_at).toLocaleString() : 'N/A',
            menu_direction: item.menu_direction,
            menu_direction_type: item.menu_direction_type,
            menu_direction_value: item.menu_direction_value,
            comission: item.comission,
            special_comission: item.special_comission
          }));
          
          setRestaurants(transformedData);
          
          const totalMenus = transformedData.length;
          const totalItems = transformedData.reduce((sum, item) => sum + item.items, 0);
          const activeMenus = transformedData.filter(item => item.status === 'Active').length;
          const pending = transformedData.filter(item => item.status === 'Inactive').length;
          
          setStats({ totalMenus, totalItems, activeMenus, pending });
        } else {
          setRestaurants([]);
          setStats({ totalMenus: 0, totalItems: 0, activeMenus: 0, pending: 0 });
        }
      } else {
        setError(response?.message || 'Failed to fetch data');
        setRestaurants([]);
      }
    } catch (error) {
      setError(error.message || 'Server error while fetching data');
      setRestaurants([]);
    } finally {
      setLoading(false);
    }
  };

  const getDirectionText = (item) => {
    if (!item.is_menu || item.is_menu === 0) return 'Neutral';
    if (item.menu_direction_type === 'Percentage') return `${item.menu_direction_value || 0}%`;
    return item.menu_direction || 'Neutral';
  };

  const getCommissionText = (item) => {
    const comission = item.comission || 0;
    const specialComission = item.special_comission || 0;
    return `${comission}% (Sp: ${specialComission}%)`;
  };

  const handleMenuToggle = async (id, currentStatus) => {
    const currentNum = Number(currentStatus);
    const newStatus = currentNum === 1 ? 0 : 1;
    
    setRestaurants(prev => 
      prev.map(item => item.id === id ? { ...item, is_menu: newStatus, _updating: true } : item)
    );
    setMenuLoading(id);
    
    try {
      const response = await postData("Menu/change_menu_status", { id, is_menu: newStatus });
      
      if (response && response.status === true) {
        setRestaurants(prev => 
          prev.map(item => item.id === id ? { ...item, is_menu: newStatus, _updating: false } : item)
        );
        await fetchRestaurants();
      } else {
        setRestaurants(prev => 
          prev.map(item => item.id === id ? { ...item, is_menu: currentNum, _updating: false } : item)
        );
        alert(response?.message || 'Failed to change menu status');
      }
    } catch (error) {
      setRestaurants(prev => 
        prev.map(item => item.id === id ? { ...item, is_menu: currentNum, _updating: false } : item)
      );
      alert('Server error while changing menu status');
    } finally {
      setMenuLoading(null);
    }
  };

  const handleViewRestaurant = (restaurant) => {
    navigate('/Dashboard/Menus', {
      state: {
        designation_id: restaurant.id,
        restaurantName: restaurant.name,
        restaurantData: restaurant
      }
    });
  };

  const getInitials = (name) => {
    if (!name || name === 'N/A') return '??';
    const words = name.trim().split(' ');
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0].charAt(0) + (words[1]?.charAt(0) || '')).toUpperCase();
  };

  const getAvatarColor = (id) => {
    const colors = [
      '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', 
      '#10b981', '#06b6d4', '#ef4444', '#14b8a6',
      '#f97316', '#6366f1', '#84cc16', '#0ea5e9'
    ];
    return colors[id % colors.length];
  };

  const filteredRestaurants = restaurants.filter(restaurant => {
    const matchesSearch = restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         restaurant.contact.includes(searchTerm) ||
                         restaurant.id.toString().includes(searchTerm);
    
    const matchesStatus = statusFilter === 'All' || restaurant.status === statusFilter;
    
    let matchesMenu = true;
    if (menuFilter === 'ON') matchesMenu = restaurant.is_menu === 1;
    else if (menuFilter === 'OFF') matchesMenu = restaurant.is_menu === 0;
    
    return matchesSearch && matchesStatus && matchesMenu;
  });

  const StatCard = ({ icon, label, value }) => (
    <div style={{
      backgroundColor: theme.surface,
      borderRadius: '8px',
      border: `1px solid ${theme.border}`,
      padding: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      transition: 'all 0.2s ease'
    }}>
      <div style={{ fontSize: '24px' }}>{icon}</div>
      <div>
        <div style={{ fontSize: '20px', fontWeight: 'bold', color: theme.text }}>{value}</div>
        <div style={{ fontSize: '12px', color: theme.textSub }}>{label}</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.3s ease'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader className="animate-spin" size={48} color={theme.primary} style={{ margin: '0 auto 16px', display: 'block' }} />
          <p style={{ color: theme.textSub }}>Loading restaurants...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.3s ease'
      }}>
        <div style={{
          textAlign: 'center',
          backgroundColor: theme.surface,
          padding: '32px',
          borderRadius: '12px',
          border: `1px solid ${theme.border}`,
          boxShadow: isDark ? '0 10px 15px -3px rgba(0,0,0,0.5)' : '0 10px 15px -3px rgba(0,0,0,0.1)',
          maxWidth: '448px',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ color: theme.danger, fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h3 style={{ fontSize: '20px', fontWeight: '600', color: theme.text, marginBottom: '8px' }}>Error Loading Data</h3>
          <p style={{ color: theme.textSub, marginBottom: '16px' }}>{error}</p>
          <button
            onClick={fetchRestaurants}
            style={{
              backgroundColor: theme.primary,
              color: '#ffffff',
              fontWeight: '600',
              padding: '8px 24px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.filter = 'brightness(1.1)'}
            onMouseLeave={(e) => e.target.style.filter = 'brightness(1)'}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.bg,
      padding: '16px',
      transition: 'background 0.3s ease'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header Stats - More Compact */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '10px',
          marginBottom: '16px'
        }}>
          <StatCard icon="🏪" label="Total Menus" value={stats.totalMenus} />
          <StatCard icon="🍽️" label="Total Items" value={stats.totalItems} />
          <StatCard icon="📈" label="Active Menus" value={stats.activeMenus} />
          <StatCard icon="⏰" label="Pending" value={stats.pending} />
        </div>

        {/* Action Button - Smaller */}
        <div style={{ marginBottom: '16px' }}>
          <button style={{
            backgroundColor: theme.primary,
            color: '#ffffff',
            fontWeight: '600',
            padding: '6px 16px',
            borderRadius: '6px',
            border: 'none',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.filter = 'brightness(1.1)'}
          onMouseLeave={(e) => e.target.style.filter = 'brightness(1)'}
          >
            <Plus size={16} />
            Initialize Menu
          </button>
        </div>

        {/* Search and Filter Bar - Complete */}
        <div style={{
          backgroundColor: theme.surface,
          borderRadius: '6px',
          border: `1px solid ${theme.border}`,
          padding: '12px 16px',
          marginBottom: '16px',
          transition: 'all 0.3s ease'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '12px',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {/* Search Bar */}
            <div style={{
              flex: 1,
              position: 'relative',
              minWidth: '180px',
              maxWidth: '300px'
            }}>
              <Search style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: theme.textMuted,
                transition: 'color 0.2s'
              }} size={16} />
              <input
                type="text"
                placeholder="Search restaurants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 12px 6px 34px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  outline: 'none',
                  fontSize: '13px',
                  height: '34px',
                  backgroundColor: theme.surfaceAlt,
                  color: theme.text,
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = theme.primary;
                  e.target.style.boxShadow = `0 0 0 2px ${theme.primaryLight}`;
                  e.target.previousSibling.style.color = theme.primary;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = theme.border;
                  e.target.style.boxShadow = 'none';
                  e.target.previousSibling.style.color = theme.textMuted;
                }}
              />
            </div>

            {/* Filter Group Label */}
            <span style={{
              fontSize: '12px',
              fontWeight: '600',
              color: theme.textSub,
              marginRight: '4px'
            }}>
              Status:
            </span>

            {/* Status Filter Buttons */}
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {['All', 'Active', 'Inactive'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontWeight: '500',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: statusFilter === status ? theme.primary : theme.surfaceAlt,
                    color: statusFilter === status ? '#ffffff' : theme.textSub,
                    fontSize: '12px',
                    height: '30px'
                  }}
                >
                  {status}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div style={{
              width: '1px',
              height: '28px',
              backgroundColor: theme.border
            }} />

            {/* Menu Filter Label */}
            <span style={{
              fontSize: '12px',
              fontWeight: '600',
              color: theme.textSub,
              marginRight: '4px'
            }}>
              Menu:
            </span>

            {/* Menu Filter Buttons */}
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {['All', 'ON', 'OFF'].map(option => (
                <button
                  key={option}
                  onClick={() => setMenuFilter(option)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontWeight: '500',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: menuFilter === option ? theme.success : theme.surfaceAlt,
                    color: menuFilter === option ? '#ffffff' : theme.textSub,
                    fontSize: '12px',
                    height: '30px'
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Restaurant Table - Compact with no horizontal scroll */}
        <div style={{
          backgroundColor: theme.surface,
          borderRadius: '8px',
          border: `1px solid ${theme.border}`,
          overflow: 'hidden',
          transition: 'all 0.3s ease'
        }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            tableLayout: 'fixed'
          }}>
            <thead>
              <tr style={{ backgroundColor: theme.surfaceAlt, borderBottom: `1px solid ${theme.border}`, transition: 'background 0.3s ease' }}>
                <th style={{ width: '28px', padding: '8px 6px', textAlign: 'center' }}>
                  <input type="checkbox" style={{ borderRadius: '3px', width: '14px', height: '14px', accentColor: theme.primary }} />
                </th>
                <th style={{ width: '36px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: theme.textSub }}>ID</th>
                <th style={{ width: '36px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: theme.textSub }}>Pic</th>
                <th style={{ width: '120px', padding: '8px 6px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: theme.textSub }}>Restaurant</th>
                <th style={{ width: '70px', padding: '8px 6px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: theme.textSub }}>Contact</th>
                <th style={{ width: '44px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: theme.textSub }}>Items</th>
                <th style={{ width: '55px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: theme.textSub }}>Direction</th>
                <th style={{ width: '75px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: theme.textSub }}>Commission</th>
                <th style={{ width: '65px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: theme.textSub }}>Menu</th>
                <th style={{ width: '55px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: theme.textSub }}>Status</th>
                <th style={{ width: '70px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: theme.textSub }}>Updated</th>
                <th style={{ width: '45px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: theme.textSub }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRestaurants.length > 0 ? (
                filteredRestaurants.map((restaurant, idx) => {
                  const isUpdating = restaurant._updating || menuLoading === restaurant.id;
                  const initials = getInitials(restaurant.name);
                  const avatarColor = getAvatarColor(restaurant.id);
                  
                  return (
                    <tr key={restaurant.id} style={{ 
                      backgroundColor: idx % 2 === 0 ? theme.surface : theme.surfaceAlt,
                      opacity: isUpdating ? 0.7 : 1,
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => { if (!isUpdating) e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#f1f5f9'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = idx % 2 === 0 ? theme.surface : theme.surfaceAlt; }}
                    >
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                        <input type="checkbox" style={{ borderRadius: '3px', width: '14px', height: '14px', accentColor: theme.primary }} />
                      </td>
                      <td style={{ padding: '6px 6px', fontSize: '11px', fontWeight: '500', color: theme.text, textAlign: 'center' }}>{restaurant.id}</td>
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${avatarColor}, ${avatarColor}dd)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          fontWeight: '700',
                          color: '#ffffff',
                          margin: '0 auto',
                          textTransform: 'uppercase',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          {initials}
                        </div>
                      </td>
                      <td style={{ 
                        padding: '6px 6px', 
                        fontSize: '12px', 
                        fontWeight: '500', 
                        color: theme.text, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        maxWidth: '120px'
                      }}>
                        <span title={restaurant.name}>{restaurant.name}</span>
                      </td>
                      <td style={{ padding: '6px 6px', fontSize: '11px', color: theme.textSub }}>{restaurant.contact}</td>
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '1px 6px',
                          borderRadius: '9999px',
                          fontSize: '11px',
                          fontWeight: '600',
                          backgroundColor: restaurant.items > 0 ? theme.successLight : theme.surfaceAlt,
                          color: restaurant.items > 0 ? theme.success : theme.textSub,
                          minWidth: '20px'
                        }}>
                          {restaurant.items}
                        </span>
                      </td>
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                        {restaurant.items === 0 ? (
                          <span style={{ color: theme.textSub, fontSize: '11px' }}>−</span>
                        ) : (
                          <span style={{ color: theme.success, fontWeight: '500', fontSize: '10px' }}>{restaurant.direction}</span>
                        )}
                      </td>
                      <td style={{ padding: '6px 6px', fontSize: '10px', color: theme.textSub, textAlign: 'center' }}>{restaurant.commission}</td>
                      
                      {/* Menu Toggle - Compact */}
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <button
                            onClick={() => handleMenuToggle(restaurant.id, restaurant.is_menu)}
                            disabled={isUpdating}
                            style={{
                              position: 'relative',
                              width: '38px',
                              height: '20px',
                              borderRadius: '10px',
                              border: 'none',
                              cursor: isUpdating ? 'not-allowed' : 'pointer',
                              background: restaurant.is_menu === 1 ? theme.success : theme.border,
                              transition: 'background 0.25s ease',
                              padding: 0,
                              opacity: isUpdating ? 0.55 : 1,
                              flexShrink: 0
                            }}
                          >
                            <span
                              style={{
                                position: 'absolute',
                                top: '2px',
                                left: '2px',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                background: '#ffffff',
                                transform: restaurant.is_menu === 1 ? 'translateX(18px)' : 'translateX(0)',
                                transition: 'transform 0.25s ease',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '8px'
                              }}
                            >
                              {isUpdating
                                ? <Loader size={8} className="animate-spin" color={theme.textSub} />
                                : restaurant.is_menu === 1
                                  ? <span style={{ color: theme.success, fontWeight: 700 }}>✓</span>
                                  : <span style={{ color: theme.textMuted, fontWeight: 700 }}>✕</span>
                              }
                            </span>
                          </button>
                          <span style={{
                            fontSize: '10px',
                            fontWeight: 500,
                            minWidth: '18px',
                            color: isUpdating ? theme.textMuted : restaurant.is_menu === 1 ? theme.success : theme.textMuted
                          }}>
                            {isUpdating ? '...' : restaurant.is_menu === 1 ? 'ON' : 'OFF'}
                          </span>
                        </div>
                      </td>
                      
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '1px 6px',
                          borderRadius: '9999px',
                          fontSize: '10px',
                          fontWeight: '600',
                          backgroundColor: restaurant.status === 'Active' ? theme.successLight : theme.dangerLight,
                          color: restaurant.status === 'Active' ? theme.successText : theme.dangerText
                        }}>
                          {restaurant.status === 'Active' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '6px 6px', fontSize: '9px', color: theme.textSub, textAlign: 'center' }}>
                        {restaurant.updated}
                      </td>
                      
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleViewRestaurant(restaurant)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: theme.primary,
                            padding: '2px 6px',
                            borderRadius: '4px',
                            transition: 'all 0.2s',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '2px',
                            fontSize: '11px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = theme.primaryLight;
                            e.target.style.color = theme.primary;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = theme.primary;
                          }}
                        >
                          <Eye size={13} />
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="12" style={{ padding: '32px 24px', textAlign: 'center', color: theme.textSub }}>
                    <div style={{ fontSize: '32px', marginBottom: '6px' }}>📭</div>
                    <p style={{ fontSize: '13px', color: theme.text }}>No restaurants found</p>
                    <p style={{ fontSize: '12px', marginTop: '2px' }}>Try adjusting your search or filters</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Results Count */}
        <div style={{ marginTop: '10px', fontSize: '12px', color: theme.textSub }}>
          Showing {filteredRestaurants.length} of {restaurants.length} restaurants
        </div>
      </div>
    </div>
  );
}